/**
 * Services-Store. Hält die Liste aller Cloud-Services und mergt
 * eingehende WebSocket-Events in den lokalen State. Stellt zusätzlich
 * abgeleitete Stats bereit (totalPlayers, byState, ...) — eine Single
 * Source of Truth für den Header und die Übersicht.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { ServiceDto, ServiceState } from '@/types/api';
import { servicesApi } from '@/api';

export const useServicesStore = defineStore('services', () => {
  const items = ref<ServiceDto[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Phase 26: Hook für Console-Komponenten — feuert wenn ein Service neu
   * registriert wird oder der State von DEAD/CRASHED/STOPPING zurück nach
   * RUNNING/STARTING springt. ConsolePanel/ConsoleView clearen ihren
   * lines-Buffer dann, sodass keine Pre-Restart-Logs mehr sichtbar sind.
   */
  const _restartListeners: Array<(serviceName: string) => void> = [];
  function onServiceRestart(cb: (serviceName: string) => void): () => void {
    _restartListeners.push(cb);
    return () => {
      const i = _restartListeners.indexOf(cb);
      if (i >= 0) _restartListeners.splice(i, 1);
    };
  }

  // -------- Abgeleitete Werte für Header / Übersicht --------------------
  const totalPlayers = computed(() =>
    items.value
      .filter((s) => s.state === 'RUNNING')
      .reduce((sum, s) => sum + (s.onlinePlayers || 0), 0)
  );

  const totalCapacity = computed(() =>
    items.value
      .filter((s) => s.state === 'RUNNING')
      .reduce((sum, s) => sum + (s.maxPlayers || 0), 0)
  );
  const runningCount = computed(() =>
    items.value.filter((s) => s.state === 'RUNNING').length
  );
  const startingCount = computed(() =>
    items.value.filter((s) => s.state === 'STARTING').length
  );
  const crashedCount = computed(() =>
    items.value.filter((s) => s.state === 'CRASHED').length
  );

  const byState = computed<Record<ServiceState, number>>(() => {
    const out: Record<ServiceState, number> = {
      STARTING: 0, RUNNING: 0, STOPPING: 0, CRASHED: 0, DEAD: 0, MAINTENANCE: 0,
    };
    for (const s of items.value) out[s.state]++;
    return out;
  });

  const byGroup = computed<Record<string, ServiceDto[]>>(() => {
    const out: Record<string, ServiceDto[]> = {};
    for (const svc of items.value) {
      (out[svc.groupName] ??= []).push(svc);
    }
    return out;
  });

  async function fetchAll() {
    loading.value = true;
    error.value = null;
    try {
      items.value = await servicesApi.list();
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
    } finally {
      loading.value = false;
    }
  }

  function applyServiceUpdate(payload: {
    name: string;
    group: string;
    state: string;
    online: number;
    max: number;
  }) {
    const idx = items.value.findIndex((s) => s.name === payload.name);
    if (idx === -1) {
      items.value.push({
        name: payload.name,
        groupName: payload.group,
        wrapperId: '',
        state: payload.state as ServiceState,
        onlinePlayers: payload.online,
        maxPlayers: payload.max,
        tps: 0,
        usedMemoryMb: 0,
        maxMemoryMb: 0,
        host: null,
        port: 0,
        startedAt: new Date().toISOString(),
        lastHeartbeat: null,
        // Schritt 8: optimistic stub — Felder werden vom nächsten Heartbeat überschrieben
        ready: false,
        readyAt: null,
        properties: {},
      });
      // Phase 26: neuer Service → Restart-Listener feuern (= Console clearen
      // wenn jemand auf diesen Service guckt)
      _restartListeners.forEach((cb) => cb(payload.name));
    } else {
      const previous = items.value[idx];
      // Phase 26: Restart-Detection. Wenn der State von DEAD/CRASHED/STOPPING
      // auf STARTING/RUNNING springt, ist das ein Restart — Console clearen.
      const wasDead = previous.state === 'DEAD'
          || previous.state === 'CRASHED'
          || previous.state === 'STOPPING';
      const nowAlive = payload.state === 'STARTING'
          || payload.state === 'RUNNING';
      if (wasDead && nowAlive) {
        _restartListeners.forEach((cb) => cb(payload.name));
      }
      items.value[idx] = {
        ...previous,
        state: payload.state as ServiceState,
        onlinePlayers: payload.online,
        maxPlayers: payload.max,
      };
    }
  }

function applyServiceAnnounced(payload: {
    name: string;
    group: string;
    host: string;
    port: number;
  }) {
    const idx = items.value.findIndex((s) => s.name === payload.name);
    if (idx === -1) {
      items.value.push({
        name: payload.name,
        groupName: payload.group,
        wrapperId: '',
        state: 'STARTING',
        onlinePlayers: 0,
        maxPlayers: 0,
        tps: 0,
        usedMemoryMb: 0,
        maxMemoryMb: 0,
        host: payload.host,
        port: payload.port,
        startedAt: new Date().toISOString(),
        lastHeartbeat: null,
        ready: false,
        readyAt: null,
        properties: {},
      });
    } else {
      items.value[idx] = {
        ...items.value[idx],
        host: payload.host,
        port: payload.port,
      };
    }
  }

  function removeService(name: string) {
    items.value = items.value.filter((s) => s.name !== name);
  }

  return {
    items, loading, error,
    totalPlayers, totalCapacity, runningCount, startingCount, crashedCount,
    byState, byGroup,
    fetchAll, applyServiceUpdate, applyServiceAnnounced, removeService,
    onServiceRestart,
  };
});